import React, { useState, useEffect } from 'react';
import { formatTime } from '../../utils/formatters';
import { styles, colors } from '../../styles/globalStyles';

export const VagaItem = ({
  vaga,
  user,
  onAgendar,
  onDesmarcar,
  onSolicitarWaitlist,
  onSairDaWaitlist,
  isAdminView,
  onEditarVaga,
  onTrancarVaga,
  onDestrancarVaga,
  onLiberarVaga,
  naFila,
  posicaoNaFila,
  waitlistId,
  userVagasNoDia = [],
  createApi = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filaAdmin, setFilaAdmin] = useState(null);
  const [loadingFila, setLoadingFila] = useState(false);
  
  const dataVaga = vaga.data_hora ? new Date(vaga.data_hora) : null;
  const horaFormatada = dataVaga ? formatTime(dataVaga) : '';
  const diaSemana = dataVaga ? dataVaga.toLocaleDateString('pt-BR', { weekday: 'short' }) : '';
  
  const isBooked = vaga.agendamento_id;
  const isFixed = vaga.aluno_fixo_id;
  const isDraft = vaga.rascunho;
  const isLocked = vaga.trancada;
  const isAvailable = !isBooked && !isFixed && !isDraft && !isLocked;
  
  const isMyBooking = isBooked && (
    (user.googleId && vaga.aluno_google_id === user.googleId) ||
    (user.id && vaga.aluno_id && vaga.aluno_id === user.id)
  );

  const isUserInWaitlist = naFila;

  // Verificar se o usuário já tem vaga neste dia (excluindo a própria vaga se for a mesma)
  const temVagaNoMesmoDia = userVagasNoDia.some(vagaUsuario => {
    if (vagaUsuario.id === vaga.id) return false; // Não comparar com a mesma vaga
    
    const dataVagaAtual = vaga.data_hora ? new Date(vaga.data_hora) : null;
    const dataVagaUsuario = vagaUsuario.data_hora ? new Date(vagaUsuario.data_hora) : null;
    
    if (!dataVagaAtual || !dataVagaUsuario) return false;
    
    // Comparar apenas dia, mês e ano (ignorar hora)
    return dataVagaAtual.getDate() === dataVagaUsuario.getDate() &&
           dataVagaAtual.getMonth() === dataVagaUsuario.getMonth() &&
           dataVagaAtual.getFullYear() === dataVagaUsuario.getFullYear();
  });

  // Obter informações da vaga existente do usuário no mesmo dia
  const vagaExistenteNoDia = temVagaNoMesmoDia 
    ? userVagasNoDia.find(vagaUsuario => {
        const dataVagaAtual = vaga.data_hora ? new Date(vaga.data_hora) : null;
        const dataVagaUsuario = vagaUsuario.data_hora ? new Date(vagaUsuario.data_hora) : null;
        
        if (!dataVagaAtual || !dataVagaUsuario) return false;
        
        return dataVagaAtual.getDate() === dataVagaUsuario.getDate() &&
               dataVagaAtual.getMonth() === dataVagaUsuario.getMonth() &&
               dataVagaAtual.getFullYear() === dataVagaUsuario.getFullYear();
      })
    : null;

  const getContainerColors = () => {
    // Waitlist tem prioridade sobre aviso de "mesmo dia" — o aluno entrou
    // intencionalmente na fila enquanto tem outra vaga no dia
    if (isUserInWaitlist) {
      return {
        border: '#c084fc',
        bg: 'linear-gradient(135deg, #faf5ff 0%, #e9d5ff 100%)',
        shadow: '0 2px 12px rgba(192, 132, 252, 0.25)',
        sidebar: '#8b5cf6'
      };
    }
    if (isLocked && isFixed) {
      return {
        border: '#f59e0b',
        bg: 'linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%)',
        shadow: '0 2px 12px rgba(245, 158, 11, 0.15)',
        sidebar: '#d97706'
      };
    }
    if (isLocked) {
      return {
        border: '#fca5a5',
        bg: 'linear-gradient(135deg, #fff5f5 0%, #fee 100%)',
        shadow: '0 2px 12px rgba(252, 165, 165, 0.15)',
        sidebar: '#ef4444'
      };
    }
    if (isDraft) {
      return {
        border: '#fbbf24',
        bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        shadow: '0 2px 12px rgba(251, 191, 36, 0.15)',
        sidebar: '#f59e0b'
      };
    }
    if (isFixed) {
      return {
        border: '#f59e0b',
        bg: 'linear-gradient(135deg, #fffbeb 0%, #fed7aa 100%)',
        shadow: '0 2px 12px rgba(245, 158, 11, 0.15)',
        sidebar: '#d97706'
      };
    }
    if (isBooked) {
      if (isMyBooking) {
        return {
          border: '#8b5cf6',
          bg: 'linear-gradient(135deg, #f5f3ff 0%, #ddd6fe 100%)',
          shadow: '0 2px 12px rgba(139, 92, 246, 0.2)',
          sidebar: '#8b5cf6'
        };
      }
      return {
        border: '#93c5fd',
        bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        shadow: '0 2px 12px rgba(147, 197, 253, 0.15)',
        sidebar: '#3b82f6'
      };
    }
    return {
      border: '#10b981',
      bg: 'linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%)',
      shadow: '0 2px 12px rgba(16, 185, 129, 0.15)',
      sidebar: '#10b981'
    };
  };

  const getBadgeColors = () => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    };

    if (isUserInWaitlist) {
      return {
        ...baseStyle,
        backgroundColor: '#e9d5ff',
        color: '#7c3aed',
        border: '1px solid #c084fc'
      };
    }
    if (isLocked && isFixed) {
      return {
        ...baseStyle,
        backgroundColor: '#fed7aa',
        color: '#9a3412',
        border: '1px solid #f59e0b'
      };
    }
    if (isLocked) {
      return {
        ...baseStyle,
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: '1px solid #fca5a5'
      };
    }
    if (isDraft) {
      return {
        ...baseStyle,
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fbbf24'
      };
    }
    if (isFixed) {
      return {
        ...baseStyle,
        backgroundColor: '#fed7aa',
        color: '#9a3412',
        border: '1px solid #f59e0b'
      };
    }
    if (isBooked) {
      if (isMyBooking) {
        return {
          ...baseStyle,
          backgroundColor: '#ddd6fe',
          color: '#5b21b6',
          border: '1px solid #8b5cf6'
        };
      }
      return {
        ...baseStyle,
        backgroundColor: '#dbeafe',
        color: '#1e40af',
        border: '1px solid #93c5fd'
      };
    }
    return {
      ...baseStyle,
      backgroundColor: '#bbf7d0',
      color: '#065f46',
      border: '1px solid #10b981'
    };
  };

  const getStatusText = () => {
    
    {/*  
        
     if (temVagaNoMesmoDia && !isMyBooking) {
      return '⚠️ Mesmo Dia';
    }
    
    */}
      
    if (isUserInWaitlist) {
      return `🎯 ${posicaoNaFila}º na Fila`;
    }
    if (isLocked && isFixed) return '🔒(Ocupada)';
    if (isLocked) return '🔒 Trancada';
    if (isDraft) return '📝 Rascunho';
    if (isFixed) return '⭐ Fixo';
    if (isBooked) return isMyBooking ? '✅ Seu Horário' : '👤 Ocupada';
    return '🟢 Disponível';
  };

  const getSecondaryStatusText = () => {
    if (temVagaNoMesmoDia && vagaExistenteNoDia && !isMyBooking) {
      const horaExistente = vagaExistenteNoDia.data_hora ? 
        formatTime(new Date(vagaExistenteNoDia.data_hora)) : '';
      return `Você já tem vaga às ${horaExistente}`;
    }
    
    if (isUserInWaitlist && isBooked && !isMyBooking) {
      return 'Vaga ocupada por outro';
    }
    return null;
  };

  useEffect(() => {
    if (!isAdminView || !expanded || !createApi || !vaga.id) return;
    setLoadingFila(true);
    const api = createApi();
    api.get(`/admin/waitlist/${vaga.id}`)
      .then(res => setFilaAdmin(res.data.fila || []))
      .catch(() => setFilaAdmin([]))
      .finally(() => setLoadingFila(false));
  }, [isAdminView, expanded, vaga.id, createApi]);

  const removerDaFila = async (waitlistEntryId) => {
    if (!createApi) return;
    if (!window.confirm('Remover este aluno da fila de espera?')) return;
    try {
      const api = createApi();
      await api.delete(`/admin/waitlist/${waitlistEntryId}`);
      setFilaAdmin(prev => prev.filter(e => e.id !== waitlistEntryId));
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAction = async (action, ...args) => {
    if (loading) return;
    
    setLoading(true);
    try {
      await action(...args);
    } catch (error) {
      console.error('Erro na ação:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionButton = (config) => {
    const { 
      label, 
      onClick, 
      type = 'primary',
      icon = null,
      fullWidth = true,
      confirmMessage = null,
      small = true,
      disabled = false
    } = config;

    const buttonStyles = {
      primary: {
        ...styles.button,
        ...styles.buttonSuccess,
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
      },
      outline: {
        ...styles.button,
        ...styles.buttonOutline,
        borderColor: colors.primary,
        color: colors.primary,
        padding: '8px 12px'
      },
      danger: {
        ...styles.button,
        ...styles.buttonDanger,
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)'
      },
      warning: {
        ...styles.button,
        ...styles.buttonWarning,
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)'
      },
      violet: {
        ...styles.button,
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        color: colors.white,
        border: 'none',
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
      },
      ghost: {
        ...styles.button,
        backgroundColor: 'transparent',
        color: colors.dark,
        border: '1px solid #e5e7eb'
      }
    };

    const baseStyle = {
      ...buttonStyles[type],
      width: fullWidth ? '100%' : 'auto',
      fontSize: '12px',
      padding: small ? '6px 10px' : '8px 12px',
      borderRadius: '8px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      transition: 'all 0.2s ease',
      opacity: (loading || disabled) ? 0.6 : 1,
      cursor: (loading || disabled) ? 'not-allowed' : 'pointer',
      minHeight: '32px'
    };

    return (
      <button
        onClick={() => {
          if (disabled) return;
          if (confirmMessage && !window.confirm(confirmMessage)) return;
          onClick();
        }}
        disabled={loading || disabled}
        style={baseStyle}
      >
        {loading ? (
          <>
            <div style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: 'white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ fontSize: '11px' }}>Processando...</span>
          </>
        ) : (
          <>
            {icon && <span>{icon}</span>}
            <span>{label}</span>
          </>
        )}
      </button>
    );
  };

  const colorsConfig = getContainerColors();
  const secondaryStatus = getSecondaryStatusText();

  return (
    <div 
      style={{
        backgroundColor: colors.white,
        borderRadius: '10px',
        padding: '12px',
        marginBottom: '10px',
        boxShadow: colorsConfig.shadow,
        border: `1.5px solid ${colorsConfig.border}`,
        background: colorsConfig.bg,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        cursor: loading ? 'wait' : 'pointer',
        opacity: loading ? 0.7 : 1,
        animation: temVagaNoMesmoDia && !isMyBooking ? 'pulseWarning 2s infinite' : 'none'
      }}
      onClick={() => !loading && setExpanded(!expanded)}
    >
      {/* Barra lateral colorida */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: colorsConfig.sidebar,
        borderTopLeftRadius: '10px',
        borderBottomLeftRadius: '10px'
      }} />

      {/* Indicador de "mesmo dia" */}
      {/*
      {temVagaNoMesmoDia && !isMyBooking && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          backgroundColor: '#fbbf24',
          color: '#92400e',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
        
          <span>⚠️</span>
          <span>Mesmo Dia</span>
         
        </div>
      )}
       */}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: expanded ? '10px' : '8px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '6px' 
          }}>
            <div style={{
              background: temVagaNoMesmoDia && !isMyBooking
                ? 'linear-gradient(135deg, #0891b2 0%, #0284c7 100%)'
                : isUserInWaitlist
                ? 'linear-gradient(135deg, #c084fc 0%, #8b5cf6 100%)'
                : isBooked && isMyBooking 
                ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
              color: colors.white,
              width: '42px',
              height: '42px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              flexShrink: 0
            }}>
              <div style={{ fontSize: '11px', opacity: 0.9 }}>{diaSemana}</div>
              <div style={{ fontSize: '16px' }}>{horaFormatada.split(':')[0]}</div>
            </div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '700', 
                color: temVagaNoMesmoDia && !isMyBooking ? '#164e63' :
                       isUserInWaitlist ? '#7c3aed' :
                       (isBooked && isMyBooking ? '#5b21b6' : colors.dark),
                marginBottom: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>{horaFormatada}</span>
                
                {/*
                 {temVagaNoMesmoDia && !isMyBooking && (
                  <span style={{
                  fontSize: '10px',
                  color: '#92400e',
                  backgroundColor: '#fef3c7',
                  padding: '2px 6px',
                  borderRadius: '8px',
                 fontWeight: '600',
                 whiteSpace: 'nowrap',
                 border: '1px solid #fbbf24'
                }}>
                 ⚠️ MESMO DIA
                </span>
                )}
                */}
                
                
                {isUserInWaitlist && (
                  <span style={{
                    fontSize: '10px',
                    color: '#7c3aed',
                    backgroundColor: '#f5f3ff',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    🎯 FILA
                  </span>
                )}
                
                {isBooked && isMyBooking && !isUserInWaitlist && !temVagaNoMesmoDia && (
                  <span style={{
                    fontSize: '10px',
                    color: '#7c3aed',
                    backgroundColor: '#f5f3ff',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap'
                  }}>
                    ✓ SEU
                  </span>
                )}
              </div>
              
              {secondaryStatus && (
                <div style={{
                  fontSize: '11px',
                  color: temVagaNoMesmoDia ? '#164e63' : '#6b7280',
                  marginBottom: '2px',
                  fontStyle: 'italic',
                  fontWeight: temVagaNoMesmoDia ? '600' : 'normal'
                }}>
                  {secondaryStatus}
                </div>
              )}
              
              {vaga.numero_vaga && (
                <div style={{
                  fontSize: '11px',
                  color: '#94a3b8',
                  fontWeight: '500',
                  lineHeight: '1.3'
                }}>
                  Vaga {vaga.numero_vaga}
                </div>
              )}
              {vaga.descricao && !expanded && (
                <div style={{
                  fontSize: '11px',
                  color: temVagaNoMesmoDia && !isMyBooking ? '#164e63' :
                         isUserInWaitlist ? '#8b5cf6' :
                         (isBooked && isMyBooking ? '#8b5cf6' : colors.gray),
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.3',
                  fontStyle: 'italic'
                }}>
                  📝 {vaga.descricao}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <div style={getBadgeColors()}>
              {getStatusText()}
            </div>
            
            {temVagaNoMesmoDia && vagaExistenteNoDia && !isMyBooking && (
              <span style={{
                fontSize: '9px',
                color: '#164e63',
                backgroundColor: '#cffafe',
                padding: '2px 6px',
                borderRadius: '8px',
                fontStyle: 'italic',
                border: '1px solid #67e8f9'
              }}>
                Sua vaga: {formatTime(new Date(vagaExistenteNoDia.data_hora))}
              </span>
            )}
            
            {isUserInWaitlist && isBooked && !isMyBooking && (
              <span style={{
                fontSize: '9px',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                padding: '2px 6px',
                borderRadius: '8px',
                fontStyle: 'italic'
              }}>
                Aguardando liberação
              </span>
            )}
            
            {isAdminView && vaga.id && (
              <span style={{
                fontSize: '9px',
                color: colors.gray,
                backgroundColor: colors.grayLight,
                padding: '2px 6px',
                borderRadius: '8px'
              }}>
                ID: {vaga.id}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          style={{
            ...styles.button,
            ...styles.buttonGhost,
            width: '28px',
            height: '28px',
            minWidth: '28px',
            padding: 0,
            borderRadius: '50%',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '6px',
            flexShrink: 0,
            color: temVagaNoMesmoDia && !isMyBooking ? '#0891b2' :
                   isUserInWaitlist ? '#8b5cf6' :
                   (isBooked && isMyBooking ? '#8b5cf6' : colors.secondary)
          }}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {expanded && (
        <div style={{
          animation: 'slideDown 0.2s ease',
          borderTop: `1px solid ${colors.grayLight}`,
          paddingTop: '12px',
          marginTop: '12px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            {vaga.descricao && (
              <div style={{
                marginBottom: '10px',
                padding: '10px',
                backgroundColor: temVagaNoMesmoDia && !isMyBooking ? '#fffbeb' :
                                 isUserInWaitlist ? '#f5f3ff' : 
                                 (isBooked && isMyBooking ? '#f5f3ff' : '#f8fafc'),
                borderRadius: '6px',
                fontSize: '12px',
                color: temVagaNoMesmoDia && !isMyBooking ? '#92400e' :
                       isUserInWaitlist ? '#5b21b6' : 
                       (isBooked && isMyBooking ? '#5b21b6' : colors.dark),
                lineHeight: '1.4',
                border: temVagaNoMesmoDia && !isMyBooking ? '1px solid #fde68a' :
                        isUserInWaitlist ? '1px solid #ddd6fe' : 
                        (isBooked && isMyBooking ? '1px solid #ddd6fe' : 'none')
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '6px' 
                }}>
                  <span style={{ 
                    fontSize: '14px', 
                    flexShrink: 0,
                    color: temVagaNoMesmoDia && !isMyBooking ? '#f59e0b' :
                           isUserInWaitlist ? '#8b5cf6' : 
                           (isBooked && isMyBooking ? '#8b5cf6' : colors.dark)
                  }}>
                    📝
                  </span>
                  <span style={{ fontSize: '12px' }}>{vaga.descricao}</span>
                </div>
              </div>
            )}

            {/* Aviso de duplicidade mais detalhado */}
            {temVagaNoMesmoDia && vagaExistenteNoDia && !isMyBooking && (
              <div style={{
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: '#fffbeb',
                borderRadius: '8px',
                border: '2px solid #fbbf24',
                animation: 'pulseWarning 2s infinite'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ⚠️
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#92400e', 
                      fontWeight: '600'
                    }}>
                      Você já tem uma vaga agendada neste dia!
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#b45309',
                      marginTop: '2px'
                    }}>
                      Horário atual: <strong>{formatTime(new Date(vagaExistenteNoDia.data_hora))}</strong>
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#92400e',
                  lineHeight: '1.4',
                  padding: '8px',
                  backgroundColor: 'rgba(251, 191, 36, 0.2)',
                  borderRadius: '6px',
                  border: '1px solid #fde68a'
                }}>
                  <strong>Atenção:</strong> Ao agendar esta nova vaga, sua vaga atual será automaticamente cancelada e substituída por esta.
                </div>
              </div>
            )}

            {isUserInWaitlist && (
              <div style={{
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: '#f5f3ff',
                borderRadius: '8px',
                border: '1px solid #ddd6fe'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    🎯
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#7c3aed', 
                      fontWeight: '600'
                    }}>
                      Você está na fila de espera
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#8b5cf6'
                    }}>
                      Posição: <strong>{posicaoNaFila}º</strong>
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#6b7280',
                  lineHeight: '1.4',
                  padding: '6px 8px',
                  backgroundColor: 'rgba(255,255,255,0.5)',
                  borderRadius: '4px'
                }}>
                  {isBooked && !isMyBooking 
                    ? 'Você será notificado automaticamente quando esta vaga for liberada.'
                    : 'Você será notificado quando esta vaga estiver disponível para agendamento.'}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {vaga.aluno_fixo_nome && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  backgroundColor: '#fffbeb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    ⭐
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#92400e', 
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>
                      Aluno Fixo
                    </div>
                    <div style={{ 
                      color: '#78350f', 
                      fontWeight: '500',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {vaga.aluno_fixo_nome}
                    </div>
                  </div>
                </div>
              )}
              
              {vaga.aluno_nome && !isUserInWaitlist && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  backgroundColor: isBooked && isMyBooking ? '#f5f3ff' : '#eff6ff',
                  borderRadius: '6px',
                  fontSize: '12px',
                  border: isBooked && isMyBooking ? '1px solid #ddd6fe' : 'none'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: isBooked && isMyBooking ? '#8b5cf6' : '#3b82f6',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {isBooked && isMyBooking ? '👑' : '👤'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ 
                      fontSize: '10px', 
                      color: isBooked && isMyBooking ? '#5b21b6' : '#1e40af', 
                      fontWeight: '600',
                      marginBottom: '2px'
                    }}>
                      {isBooked && isMyBooking ? 'Você' : 'Aluno'}
                    </div>
                    <div style={{ 
                      color: isBooked && isMyBooking ? '#5b21b6' : '#1e3a8a', 
                      fontWeight: '500',
                      fontSize: '11px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {vaga.aluno_nome}
                    </div>
                  </div>
                </div>
              )}
              
              {vaga.criado_por_nome && isAdminView && (
                <div style={{
                  fontSize: '10px',
                  color: colors.gray,
                  padding: '6px 10px',
                  backgroundColor: colors.grayLight,
                  borderRadius: '6px',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}>
                  Criado por: <strong>{vaga.criado_por_nome}</strong>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop: '12px' }}>
            {isAdminView ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {isLocked ? (
                    getActionButton({
                      label: 'Destrancar',
                      onClick: () => handleAction(onDestrancarVaga, vaga.id),
                      type: 'success',
                      icon: '🔓',
                      small: true
                    })
                  ) : (
                    getActionButton({
                      label: 'Trancar',
                      onClick: () => handleAction(onTrancarVaga, vaga.id),
                      type: 'warning',
                      icon: '🔒',
                      small: true
                    })
                  )}
                  
                  {onEditarVaga && (
                    getActionButton({
                      label: 'Editar',
                      onClick: () => onEditarVaga(vaga),
                      type: 'outline',
                      icon: '⚙',
                      fullWidth: false,
                      small: true
                    })
                  )}
                </div>
                
                {(isBooked || isFixed) && (
                  getActionButton({
                    label: 'Liberar Vaga',
                    onClick: () => handleAction(onLiberarVaga, vaga.id),
                    type: 'danger',
                    icon: '🚪',
                    small: false,
                    confirmMessage: 'Tem certeza que deseja liberar esta vaga?'
                  })
                )}

                {/* Fila de espera admin */}
                {createApi && (
                  <div style={{ marginTop: '8px' }}>
                    {loadingFila ? (
                      <div style={{ fontSize: '11px', color: '#8b5cf6', textAlign: 'center', padding: '6px' }}>Carregando fila...</div>
                    ) : filaAdmin && filaAdmin.length > 0 ? (
                      <div style={{ backgroundColor: '#f5f3ff', borderRadius: '8px', padding: '8px', border: '1px solid #ede9fe' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', marginBottom: '6px' }}>
                          ⏳ Fila de espera ({filaAdmin.length})
                        </div>
                        {filaAdmin.map((entry, i) => (
                          <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', backgroundColor: 'white', borderRadius: '6px', marginBottom: i < filaAdmin.length - 1 ? '4px' : 0 }}>
                            <div>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: '#7c3aed', marginRight: '6px' }}>#{i + 1}</span>
                              <span style={{ fontSize: '11px', fontWeight: '600', color: '#1e293b' }}>{entry.nome}</span>
                              <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '4px' }}>{entry.email}</span>
                            </div>
                            <button
                              onClick={() => removerDaFila(entry.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '2px 4px', lineHeight: 1 }}
                              title="Remover da fila"
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    ) : filaAdmin !== null ? (
                      <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', padding: '4px' }}>Sem fila de espera</div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {isUserInWaitlist ? (
                  <>
                    <div style={{
                      textAlign: 'center',
                      padding: '8px',
                      backgroundColor: '#f5f3ff',
                      borderRadius: '8px',
                      marginBottom: '6px'
                    }}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#7c3aed',
                        fontWeight: '600',
                        marginBottom: '2px'
                      }}>
                        🎯 Posição {posicaoNaFila} na fila
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        color: '#8b5cf6' 
                      }}>
                        {isBooked && !isMyBooking 
                          ? 'Você será notificado quando o atual ocupante liberar a vaga.'
                          : 'Você será notificado quando a vaga estiver disponível.'}
                      </div>
                    </div>
                    
                    {getActionButton({
                      label: 'Sair da Fila',
                      onClick: () => handleAction(onSairDaWaitlist, vaga.id),
                      type: 'outline',
                      icon: '❌',
                      small: false,
                      confirmMessage: 'Tem certeza que deseja sair da lista de espera?'
                    })}
                  </>
                ) : (
                  <>
                    {isAvailable && (
                      getActionButton({
                        label: temVagaNoMesmoDia ? 'Trocar Vaga' : 'Agendar',
                        onClick: () => handleAction(onAgendar, vaga.id),
                        type: temVagaNoMesmoDia ? 'warning' : 'primary',
                        icon: temVagaNoMesmoDia ? '🔄' : '✅',
                        small: false,
                        confirmMessage: temVagaNoMesmoDia 
                          ? `Você já possui uma vaga agendada neste dia às ${vagaExistenteNoDia ? formatTime(new Date(vagaExistenteNoDia.data_hora)) : ''}. Deseja cancelar a vaga atual e agendar esta nova vaga às ${horaFormatada}?`
                          : `Confirmar agendamento para ${horaFormatada}?`
                      })
                    )}
                    
                    {isMyBooking && (
                      getActionButton({
                        label: 'Cancelar Agendamento',
                        onClick: () => handleAction(onDesmarcar, vaga.agendamento_id),
                        type: 'danger',
                        icon: '🗑',
                        small: false,
                        confirmMessage: 'Tem certeza que deseja cancelar este agendamento?'
                      })
                    )}
                    
                    {(isBooked || isLocked || isFixed) && !isMyBooking && (
                      getActionButton({
                        label: temVagaNoMesmoDia ? '⏰ Entrar na Fila (troca ao conseguir)' : 'Entrar na Fila de Espera',
                        onClick: () => handleAction(
                          onSolicitarWaitlist,
                          vaga.id,
                          temVagaNoMesmoDia ? vagaExistenteNoDia?.agendamento_id : undefined
                        ),
                        type: temVagaNoMesmoDia ? 'violet' : 'violet',
                        icon: temVagaNoMesmoDia ? '🔄' : '⏰',
                        small: false,
                        confirmMessage: temVagaNoMesmoDia
                          ? `Você já tem uma vaga às ${vagaExistenteNoDia ? formatTime(new Date(vagaExistenteNoDia.data_hora)) : ''}.\n\nAo entrar na fila das ${horaFormatada}, sua vaga atual será mantida.\n\nEla só será cancelada automaticamente quando você for promovido para este horário.\n\nDeseja continuar?`
                          : 'Deseja entrar na lista de espera para este horário?\n\nVocê será notificado automaticamente quando qualquer vaga deste horário for liberada.'
                      })
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulseWarning {
            0%, 100% {
              background-color: rgba(251, 191, 36, 0.1);
              border-color: #fbbf24;
            }
            50% {
              background-color: rgba(251, 191, 36, 0.2);
              border-color: #f59e0b;
            }
          }
          
          @media (max-width: 320px) {
            .vaga-container {
              padding: 10px !important;
            }
            .vaga-time-circle {
              width: 36px !important;
              height: 36px !important;
              font-size: 14px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

